'use client'

import { FC, useContext, useMemo } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import {
  getEffectiveFeaturePlanId,
  normalizePersonalPlanTier
} from '@/lib/billing/losi-pricing'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { PLAN_FREE } from '@/lib/stripe/config'
import { LosiAvatar } from '@/components/losi-bot/losi-avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  IconBolt,
  IconBrain,
  IconBrandAsana,
  IconStars,
  IconCheck,
  IconLock
} from '@tabler/icons-react'
import { useDelayedPaywall } from '@/hooks/use-delayed-paywall'

type ReasoningEffort = 'none' | 'low' | 'medium' | 'high'

interface Option {
  key: ReasoningEffort
  label: string
  description: string
  icon: React.ReactNode
}

export const ThinkSelector: FC = () => {
  const { chatSettings, setChatSettings } = useContext(ChatbotUIChatContext)
  const { isThinkDeeperEnabled, setIsThinkDeeperEnabled, profile, setIsPaywallOpen } =
    useContext(ChatbotUIContext)
  const { t } = useTranslation()
  const scheduleUpgradePaywall = useDelayedPaywall(
    setIsPaywallOpen ? () => setIsPaywallOpen(true) : undefined
  )

  const value: ReasoningEffort = (chatSettings?.reasoningEffort as ReasoningEffort) || 'none'
  const userPlan = normalizePersonalPlanTier(getEffectiveFeaturePlanId(profile))

  const options: Option[] = useMemo(
    () => [
      {
        key: 'none',
        label: t('Auto'),
        labelKey: 'Auto',
        description: t('Quick responses'),
        icon: <IconBolt size={20} className="text-muted-foreground" />
      } as any,
      {
        key: 'low',
        label: t('Expert'),
        description: t('Thinks hard'),
        icon: <IconBrain size={20} className="text-muted-foreground" />
      },
      {
        key: 'medium',
        label: t('Heavy'),
        description: t('Team of Experts'),
        icon: <IconBrandAsana size={20} className="text-muted-foreground" />
      },
      {
        key: 'high',
        label: t('Max'),
        description: t('Genius-level Experts'),
        icon: <IconStars size={20} className="text-muted-foreground" />
      }
    ],
    [t]
  )

  const handleChange = (next: ReasoningEffort) => {
    if (!chatSettings) return
    if (userPlan === PLAN_FREE && next !== 'none') {
      scheduleUpgradePaywall()
      return
    }
    setChatSettings({
      ...chatSettings,
      reasoningEffort: next
    })
    // Keep legacy toggle semantics for extended budgets (only for Max)
    setIsThinkDeeperEnabled(next === 'high')
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-lg border border-slate-200/30 p-2 hover:bg-slate-200 dark:border-slate-700/20 dark:hover:bg-slate-800"
          aria-label={t('Think level')}
        >
          <span className="text-muted-foreground flex items-center gap-1.5 text-sm">
            {options.find((o) => o.key === value)?.icon}
            <span>{options.find((o) => o.key === value)?.label}</span>
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" sideOffset={5} className="min-w-[220px]">
        {(() => {
          if (userPlan === PLAN_FREE) {
            return (
              <>
                <DropdownMenuLabel className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <LosiAvatar state="idle" size={20} />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">Losi Max</span>
                      <span className="text-muted-foreground text-xs">
                        {t('Unlock all capabilities')}
                      </span>
                    </div>
                  </div>
                  <Button variant="secondary" size="xs" onClick={() => scheduleUpgradePaywall()}>
                    {t('Upgrade')}
                  </Button>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )
          }
          return null
        })()}

        {options.map((opt) => {
          const locked = userPlan === PLAN_FREE && opt.key !== 'none'
          const selected = value === opt.key
          return (
          <DropdownMenuItem
            key={opt.key}
            className={cn(
              'flex items-center justify-between py-2',
              selected && 'bg-blue-50 dark:bg-blue-900/20'
            )}
            onClick={() => (locked ? scheduleUpgradePaywall() : handleChange(opt.key))}
          >
              <div className="flex items-center gap-2">
                <div className="shrink-0">{opt.icon}</div>
                <div className="flex flex-col">
                  <span className={cn('text-sm font-medium', locked && 'text-muted-foreground')}>
                    {opt.label}
                  </span>
                  <span className="text-muted-foreground text-xs">{opt.description}</span>
                </div>
              </div>
              <div className="ml-3 flex items-center">
                {locked ? (
                  <IconLock size={16} className="text-muted-foreground" />
                ) : selected ? (
                  <IconCheck size={16} className="text-primary" />
                ) : (
                  <div className="size-4" />
                )}
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
